<template>
  <!--
    用 Element 的 el-dialog 承载（原来是手写的 .share-mask + .share-dialog）。
    ⚠️ :modal-append-to-body="false" 是必须的：默认 true 时遮罩 .v-modal 会被挂到 document.body，
    而对话框本体留在 .frame 里（.frame 是 position:fixed，自成一个层叠上下文，z-index 只有 auto），
    于是 body 上 z-index 2001 的遮罩会盖住 z-index 2002 的对话框。
    关掉它之后遮罩会被插到对话框旁边（同一个父节点），两者同处一个层叠上下文，
    且 Element 给对话框的 z-index 一定比遮罩大 → 层级正确；顺带还能继承 .frame 上的 --lp-* 工作表配色。

    垂直居中由 App.vue 的全局 `.el-dialog__wrapper{display:flex;align-items:center}` 负责，
    这里**不要再写 top="8vh"**（它只是往下推 margin-top，做不到居中，还会和全局规则打架）。

    2026-10-06 改版：原「可见范围」（跟随工作区 / 不分享 / 好友可见 / 所有人可见）整体删除，
    改为按好友逐个授权的「权限设置」——每位好友单独指定只读 / 编辑，
    并可单独开放「分类（周累计）」统计表的查看权限；保存后后端会给对应好友发提醒。
  -->
  <el-dialog
    :visible="visible"
    title="分享我的内容给好友"
    width="600px"
    :modal-append-to-body="false"
    @close="close"
  >
    <div class="dialog-body">
      <div class="field">
        <label class="label">选择工作区</label>
        <select v-model="selectedWorkspaceId" class="select" @change="onWorkspaceChange">
          <option v-for="ws in workspaces" :key="ws.id" :value="ws.id">{{ ws.name }}</option>
        </select>
      </div>

      <div class="field">
        <label class="label">选择工作表</label>
        <select v-model="selectedProjectId" class="select" @change="onProjectChange">
          <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
        </select>
      </div>

      <div class="field">
        <label class="label">权限设置</label>
        <p class="field-hint">选择好友并授权时间表权限</p>

        <div v-if="loading" class="perm-empty">加载中…</div>

        <div v-else-if="!friends.length" class="perm-empty">
          你还没有好友，先在「朋友」页面添加好友后才能开放时间表。
        </div>

        <div v-else class="friend-list">
          <div
            v-for="f in friends"
            :key="f.friendId"
            class="friend-row"
            :class="{ granted: !!selected[String(f.friendId)] }"
          >
            <div class="friend-main">
              <UserAvatar class="friend-avatar" :url="f.avatarUrl" :name="displayName(f)" />
              <span class="friend-name" :title="displayName(f)">{{ displayName(f) }}</span>
              <input
                type="checkbox"
                class="friend-check"
                :checked="!!selected[String(f.friendId)]"
                :title="selected[String(f.friendId)] ? '取消开放' : '开放给该好友'"
                @change="toggleFriend(f, $event.target.checked)"
              >
            </div>

            <!-- 未勾选的好友不展示权限项，列表保持清爽 -->
            <div v-if="selected[String(f.friendId)]" class="friend-perms">
              <div class="seg seg-sm">
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: selected[String(f.friendId)].permission === 'READ' }"
                  @click="setPermission(f.friendId, 'READ')"
                >只读</button>
                <button
                  type="button"
                  class="seg-btn"
                  :class="{ active: selected[String(f.friendId)].permission === 'EDIT' }"
                  @click="setPermission(f.friendId, 'EDIT')"
                >编辑</button>
              </div>
              <label class="cat-toggle" :class="{ on: selected[String(f.friendId)].categoryVisible }">
                <input
                  type="checkbox"
                  class="cat-check"
                  :checked="selected[String(f.friendId)].categoryVisible"
                  @change="setCategoryVisible(f.friendId, $event.target.checked)"
                >
                <span>开放周累计表</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <p class="hint">开放或调整权限后，会自动给对应好友发送一条提醒；未勾选的好友将看不到这张工作表。</p>
    </div>

    <div slot="footer" class="dialog-foot">
      <button class="cancel-btn" @click="close">取消</button>
      <button class="save-btn" :disabled="!canSave || saving" @click="save">
        {{ saving ? '保存中…' : '保存' }}
      </button>
    </div>
  </el-dialog>
</template>

<script>
import UserAvatar from '@/components/common/UserAvatar.vue'
import { listWorkspaces } from '@/api/workspace.js'
import { listProjects } from '@/api/project.js'
import { listFriends } from '@/api/friend.js'
import { listGrants, grantProject, revokeGrant } from '@/api/share.js'

export default {
  name: 'ShareSettingDialog',
  components: { UserAvatar },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      workspaces: [],
      projects: [],
      selectedWorkspaceId: null,
      selectedProjectId: null,
      friends: [],
      /**
       * 本次编辑后的授权：friendId(string) -> { permission: 'READ'|'EDIT', categoryVisible: boolean }
       * 不在这个对象里的好友 = 不开放。
       */
      selected: {},
      /** 打开时后端已有的授权，形状同 selected，仅用于保存时做 diff */
      existing: {},
      saving: false,
      loading: false
    }
  },
  computed: {
    canSave() {
      return Boolean(this.selectedProjectId) && !this.loading
    }
  },
  watch: {
    visible(val) {
      if (val) this.loadWorkspaces()
    }
  },
  methods: {
    displayName(f) {
      return f.remark || f.nickname || '好友'
    },
    async loadWorkspaces() {
      this.loading = true
      try {
        const res = await listWorkspaces()
        if (!res.success || res.code !== 200) {
          this.workspaces = []
          return
        }
        this.workspaces = res.data || []
        if (this.workspaces.length) {
          this.selectedWorkspaceId = this.selectedWorkspaceId || this.workspaces[0].id
          await this.loadProjects()
        }
      } catch (e) {
        console.error('[ShareSettingDialog] 加载工作区失败', e)
      } finally {
        this.loading = false
      }
    },
    async loadProjects() {
      if (!this.selectedWorkspaceId) {
        this.projects = []
        return
      }
      try {
        const res = await listProjects(this.selectedWorkspaceId)
        this.projects = (res.success && res.data) ? res.data : []
        if (this.projects.length) {
          this.selectedProjectId = this.projects[0].id
          await this.loadPermissions()
        } else {
          this.selectedProjectId = null
          this.friends = []
          this.selected = {}
          this.existing = {}
        }
      } catch (e) {
        console.error('[ShareSettingDialog] 加载工作表失败', e)
      }
    },
    /**
     * 拉取「好友列表 + 该表已有授权」并合并成初始状态。
     * 好友列表以 listFriends 为准（带备注名），授权记录只用来回填权限。
     */
    async loadPermissions() {
      if (!this.selectedProjectId) return
      this.loading = true
      try {
        const [fRes, gRes] = await Promise.all([
          listFriends(),
          listGrants(this.selectedProjectId)
        ])
        this.friends = ((fRes && fRes.data) || []).map(f => ({
          friendId: f.friendId,
          nickname: f.nickname,
          remark: f.remark,
          avatarUrl: f.avatarUrl
        }))
        const existing = {}
        const selected = {}
        ;((gRes && gRes.data) || []).forEach(g => {
          const id = String(g.friendId)
          const entry = {
            permission: g.permission === 'EDIT' ? 'EDIT' : 'READ',
            categoryVisible: !!g.categoryVisible
          }
          existing[id] = entry
          selected[id] = { ...entry }
        })
        this.existing = existing
        this.selected = selected
      } catch (e) {
        console.error('[ShareSettingDialog] 加载好友 / 授权失败', e)
        this.friends = []
        this.selected = {}
        this.existing = {}
      } finally {
        this.loading = false
      }
    },
    onWorkspaceChange() {
      this.loadProjects()
    },
    onProjectChange() {
      this.loadPermissions()
    },
    toggleFriend(f, checked) {
      const id = String(f.friendId)
      if (checked) {
        // 新勾选默认「只读 + 不开放周累计表」，由用户按需再调整
        this.$set(this.selected, id, {
          permission: 'READ',
          categoryVisible: false
        })
      } else {
        this.$delete(this.selected, id)
      }
    },
    setPermission(friendId, permission) {
      const id = String(friendId)
      const entry = this.selected[id]
      if (!entry) return
      this.$set(this.selected, id, { ...entry, permission })
    },
    setCategoryVisible(friendId, categoryVisible) {
      const id = String(friendId)
      const entry = this.selected[id]
      if (!entry) return
      this.$set(this.selected, id, { ...entry, categoryVisible: !!categoryVisible })
    },
    async save() {
      if (!this.canSave) return
      const desired = this.selected
      const existing = this.existing
      const ops = []
      // 新增授权 / 权限或分类统计权有变化 → 重新授权（后端 upsert 并给好友发提醒）
      Object.keys(desired).forEach(id => {
        const want = desired[id]
        const had = existing[id]
        if (!had || had.permission !== want.permission || had.categoryVisible !== want.categoryVisible) {
          ops.push(grantProject(this.selectedProjectId, id, want.permission, want.categoryVisible))
        }
      })
      // 之前有授权、现在没勾 → 收回
      Object.keys(existing).forEach(id => {
        if (!desired[id]) {
          ops.push(revokeGrant(this.selectedProjectId, id))
        }
      })
      if (!ops.length) {
        this.close()
        return
      }
      this.saving = true
      try {
        const results = await Promise.all(ops.map(p => p.catch(e => e)))
        const failed = results.filter(r => r && (r.success === false || r.code !== 200 || r instanceof Error))
        if (failed.length) {
          const msg = failed[0] && failed[0].message ? failed[0].message : '部分授权操作失败'
          alert(msg)
          // 重新加载，保证界面与后端一致
          await this.loadPermissions()
          return
        }
        this.$emit('saved')
        this.close()
      } catch (e) {
        console.error('[ShareSettingDialog] 保存授权失败', e)
        alert('网络异常，请稍后重试')
      } finally {
        this.saving = false
      }
    },
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* 弹窗外壳（遮罩 / 白底 / 描边 / 圆角）由 App.vue 里的全局 .el-dialog 主题负责，
   这里只保留弹窗内部内容的样式。
   尺寸整体比改造前放大了一档（用户要求「悬浮窗设计得大一些」）：
   宽度 420 → 560px（2026-10-06 权限列表变宽，再提到 600px），字号 13 → 14px。
   2026-10-03 再放大一档（用户要求「文字更舒适」）：标签 / 下拉 / 选项名 / 按钮 14 → 15px，
   说明与提示 12 → 13px，行高 1.5~1.6；控件圆角同步加大。 */
.dialog-body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  color: #57795c;
}

.select {
  border: 2px solid #91a67b;
  background: #f7f7df;
  border-radius: 14px;
  padding: 10px 12px;
  font-size: 15px;
  line-height: 1.5;
  color: #263b29;
  outline: none;
}

/* ===== 权限设置 ===== */
/* 「权限设置」标签下的一行小字，说明这一块要做什么 */
.field-hint {
  margin: -2px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8a9a78;
}

.perm-empty {
  padding: 22px 8px;
  text-align: center;
  font-size: 13px;
  line-height: 1.6;
  color: #8a9a78;
}

.seg {
  display: inline-flex;
  border: 2px solid #91a67b;
  border-radius: 10px;
  overflow: hidden;
}

.seg-btn {
  border: none;
  background: #f7f7df;
  color: #57795c;
  padding: 4px 12px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.seg-btn + .seg-btn {
  border-left: 2px solid #91a67b;
}

.seg-btn.active {
  background: #f1dc7b;
  color: #263b29;
  font-weight: 700;
}

.seg-sm .seg-btn {
  padding: 3px 11px;
  font-size: 12px;
}

.friend-list {
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  border: 2px solid #91a67b;
  border-radius: 14px;
}

.friend-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 9px 12px;
  border-bottom: 1px solid #e3efe6;
}

.friend-row:last-child {
  border-bottom: none;
}

.friend-row.granted {
  background: #f7f7df;
}

.friend-main {
  display: flex;
  align-items: center;
  gap: 10px;
}

.friend-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #b8d49b;
  border: 2px solid #57795c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
}

.friend-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #263b29;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.friend-check {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: #4aa46a;
}

.friend-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding-left: 40px;
}

/* 「分类（周累计）」表查看权限：独立于只读 / 编辑的小开关 */
.cat-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #57795c;
  cursor: pointer;
  user-select: none;
}

.cat-check {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #4aa46a;
}

.cat-toggle.on {
  color: #2f7a4a;
  font-weight: 700;
}

.hint {
  font-size: 13px;
  line-height: 1.6;
  color: #8a9a78;
}

.dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn,
.save-btn {
  border: none;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  padding: 10px 22px;
  border-radius: 14px;
  cursor: pointer;
}

.cancel-btn {
  background: #f7f7df;
  color: #57795c;
}

.save-btn {
  background: #f1dc7b;
  border: 2px solid #987a2c;
  color: #263b29;
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，尺寸沿用上面的值。 */
/* 表单：标签 / 下拉 */
.label{
  color:var(--lp-text-2,#5f7268);
}
.select{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  color:var(--lp-text,#26332b);
}
/* 默认权限条 → 已删除（2026-10-06 用户要求去掉「新选好友默认权限」），
   这里只保留权限设置说明小字与空态的文字色 */
.field-hint,
.perm-empty{
  color:var(--lp-text-3,#94a59b);
}
/* 只读 / 编辑分段 */
.seg{
  border:2px solid var(--lp-border-strong,#a5cdb3);
  border-radius:0.5rem;
}
.seg-btn{
  background:var(--lp-surface,#fff);
  color:var(--lp-text-2,#5f7268);
}
.seg-btn + .seg-btn{
  border-left:2px solid var(--lp-border-strong,#a5cdb3);
}
.seg-btn.active{
  background:var(--lp-primary,#4aa46a);
  color:#fff;
}
/* 好友列表 */
.friend-list{
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.75rem;
}
.friend-row{
  border-bottom:1px solid var(--lp-border-soft,#eef5f0);
}
.friend-row.granted{
  background:var(--lp-surface-2,#f7fbf8);
}
.friend-row:hover{
  background:var(--lp-primary-soft,#e9f5ec);
}
.friend-avatar{
  background:var(--lp-primary,#4aa46a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
.friend-name{
  color:var(--lp-text,#26332b);
}
.friend-check,
.cat-check{
  accent-color:var(--lp-primary,#4aa46a);
}
.cat-toggle{
  color:var(--lp-text-2,#5f7268);
}
.cat-toggle.on{
  color:var(--lp-primary-dark,#2f7a4a);
}
.hint{
  color:var(--lp-text-3,#94a59b);
}
/* 底部按钮区：取消=描边、保存=实心主色 */
.cancel-btn{
  background:var(--lp-surface,#fff);
  border:2px solid var(--lp-border,#e3efe6);
  border-radius:0.875rem;
  color:var(--lp-text-2,#5f7268);
}
.cancel-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary-soft2,#d5ebdb);
  color:var(--lp-primary-dark,#2f7a4a);
}
.save-btn{
  background:var(--lp-primary,#4aa46a);
  border:2px solid var(--lp-primary,#4aa46a);
  border-radius:0.875rem;
  color:#fff;
}
.save-btn:hover:not(:disabled){
  background:var(--lp-primary-dark,#2f7a4a);
  border-color:var(--lp-primary-dark,#2f7a4a);
}
</style>
